<!-- Ported from the Polaris kit: src/product/components/FilterSelect.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09): the clear button's name goes through i18n; the value can be a number (0 is a
     value, not "off"); the empty value follows the mode (`[]` with `multiple`, `''` without), so a multiple filter
     bound to nothing does not hand `''` to MultiSelectInput. -->
<script setup>
// A filter in a toolbar: a small button with its name and its value ("Status · Open") that opens the options (one, or several with
// `multiple`). When it is on, it can be cleared with its own button. Built from SearchSelect and MultiSelectInput.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Close16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import MultiSelectInput from './MultiSelectInput.vue';
import SearchSelect from './SearchSelect.vue';

const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, required: true },
  multiple: Boolean,
  searchable: { type: Boolean, default: true },
});
// No default: an unset filter is `[]` with `multiple` and `''` without (a number, 0 included, is a value).
const model = defineModel({
  type: [String, Number, Array],
  default: undefined,
});
const { t } = useI18n();
const value = computed({
  get: () => {
    if (props.multiple) return Array.isArray(model.value) ? model.value : [];
    return model.value ?? '';
  },
  set: next => {
    model.value = next;
  },
});
const on = computed(() =>
  props.multiple ? value.value.length > 0 : value.value !== ''
);
const clear = () => {
  model.value = props.multiple ? [] : '';
};
</script>

<template>
  <div class="flex items-center gap-1" data-filter-select :data-on="on">
    <div class="w-44">
      <MultiSelectInput
        v-if="multiple"
        v-model="value"
        :options="options"
        :label="label"
        :placeholder="label"
      />
      <SearchSelect
        v-else
        v-model="value"
        :options="options"
        :label="label"
        :placeholder="label"
        :searchable="searchable"
      />
    </div>
    <Button
      v-if="on"
      type="button"
      variant="ghost"
      size="icon-sm"
      :aria-label="t('POLARIS.SELECT.CLEAR_FILTER', { label })"
      @click="clear"
    >
      <Close16 aria-hidden="true" />
    </Button>
  </div>
</template>
